import type { Blueprint } from '@builder/base/blueprints/Blueprint';
import type { ValidatableBlueprint } from '@builder/base/blueprints/ValidatableBlueprint';
import type { HasWidth } from '@builder/base/traits/HasWidth';
import type { ValidationErrors } from '@builder/base/types/ValidationErrors';
import type { FormBuilderContract } from '@builder/form';
import type { BlueprintDefinition } from '@builder/form/blueprints/BlueprintDefinition';
import type { CustomErrorBlueprint } from '@builder/form/blueprints/CustomErrorBlueprint';
import type { ReadonlyBlueprint } from '@builder/form/blueprints/ReadonlyBlueprint';
import type { RequiredBlueprint } from '@builder/form/blueprints/RequiredBlueprint';
import type { VisibleBlueprint } from '@builder/form/blueprints/VisibleBlueprint';
import type { SortableBlueprint } from '@builder/form/blueprints/SortableBlueprint';
import type { Entry } from '@builder/form/entries/Entry';
import { entry } from '@builder/form/entries/Entry';
import { ValidEntry } from '@builder/form/entries/ValidEntry';
import { AlwaysChoice } from '@builder/form/enums/AlwaysChoice';
import type { InternallyChoice } from '@builder/form/enums/InternallyChoice';
import { NeverChoice } from '@builder/form/enums/NeverChoice';
import type { WhenChoice } from '@builder/form/enums/WhenChoice';
import type { EntryFactory } from '@builder/form/traits/EntryFactory';
import type { HasAffix } from '@builder/form/traits/HasAffix';
import type { HasFilter } from '@builder/form/traits/HasFilter';
import type { HasHelp } from '@builder/form/traits/HasHelp';
import type { HasLabel } from '@builder/form/traits/HasLabel';
import type { HasPlaceholder } from '@builder/form/traits/HasPlaceholder';
import type { IsIndexable } from '@builder/form/traits/IsIndexable';
import type { AffixValue } from '@builder/form/types/AffixValue';
import type { ProcessCallback } from '@builder/form/types/ProcessCallback';

export const Definition: BlueprintDefinition = {
    type: 'url',
    name: '[[[Adres URL]]]',
    icon: 'fa-link',
    group: 'special',
    position: 2
};

export class UrlEntry extends ValidEntry<string>
{
    public type: string = Definition.type;
    public data: string = null;

    public constructor(data: any = null)
    {
        super();

        if (data !== null)
        {
            this.data = data;
        }
    }

    public async collect(blueprint: UrlContract, form: FormBuilderContract, preprocess: ProcessCallback): Promise<Entry>
    {
        const result = await preprocess(blueprint, this, form.blueprintId, form.entryId);

        const readonly = form.expressions.readonly(blueprint);
        const data = this.data == null || (readonly && blueprint.defaultValue) ? form.expressions.executeExpression(blueprint.defaultValue) : this.data;

        return entry({
            type: this.type,
            data,
            ...(result ?? {})
        });
    }

    public validate(blueprint: UrlContract, form: FormBuilderContract): boolean
    {
        this.errors = {};

        const readonly = form.expressions.readonly(blueprint);
        const data = this.data == null || (readonly && blueprint.defaultValue) ? form.expressions.executeExpression(blueprint.defaultValue) : this.data;

        if (!form.expressions.readonly(blueprint, true) && form.expressions.visible(blueprint, true))
        {
            if (form.expressions.required(blueprint) && (data == null || data.length == 0))
            {
                this.errors.value = [`[[[Pole "%0" jest wymagane.|||${form.localization.translate(blueprint.label)}]]]`];
            }
            else if ((data != null && data.length > 0) && !this.validUrl(data))
            {
                this.errors.value = [`[[[Pole "%0" nie zawiera prawidłowego adresu URL.|||${form.localization.translate(blueprint.label)}]]]`];
            }
            else if (form.expressions.customError(blueprint))
            {
                this.errors.custom = [form.expressions.customErrorMessage(blueprint)];
            }
        }

        return this.valid();
    }

    private validUrl(url: string): boolean
    {
        try
        {
            new URL(url);

            return true;
        }
        catch (e)
        {
            return false;
        }
    }
}

export const instanceOfUrlEntry = (object: any): object is UrlEntry =>
{
    return object && 'type' in object && object.type === Definition.type;
};

export interface UrlContract extends Blueprint, VisibleBlueprint, ReadonlyBlueprint, RequiredBlueprint, CustomErrorBlueprint, SortableBlueprint, HasLabel, HasPlaceholder, HasAffix, HasHelp, HasWidth, HasFilter, IsIndexable
{
    defaultValue: string;
}

export class UrlType implements UrlContract, ValidatableBlueprint, EntryFactory<UrlEntry>
{
    public id: string;
    public type: string;
    public name: string;
    public label: Record<string, string>;
    public showLabel: boolean;
    public placeholder: Record<string, string>;
    public defaultValue: string;
    public help: Record<string, string>;
    public affix: AffixValue;
    public minWidth: number;
    public width: number;
    public visible: AlwaysChoice | NeverChoice | InternallyChoice | WhenChoice;
    public visibleWhen: string;
    public readonly: AlwaysChoice | NeverChoice | InternallyChoice | WhenChoice;
    public readonlyWhen: string;
    public required: AlwaysChoice | NeverChoice | WhenChoice;
    public requiredWhen: string;
    public customError: NeverChoice | WhenChoice;
    public customErrorWhen: string;
    public customErrorMessage: Record<string, string>;
    public errors: ValidationErrors;
    public showFilter: boolean;
    public indexEnabled: boolean;
    public sortable: boolean;

    public constructor(id: string, name: string)
    {
        this.id = id;
        this.type = Definition.type;
        this.name = name;
        this.label = { 'pl-PL': 'Adres URL' };
        this.showLabel = true;
        this.placeholder = {};
        this.defaultValue = '';
        this.help = {};
        this.affix = { prepend: '', append: '' };
        this.minWidth = 1;
        this.width = 0;
        this.customError = NeverChoice.Never;
        this.customErrorWhen = null;
        this.customErrorMessage = {};
        this.readonly = NeverChoice.Never;
        this.readonlyWhen = null;
        this.required = NeverChoice.Never;
        this.requiredWhen = null;
        this.visible = AlwaysChoice.Always;
        this.visibleWhen = null;
        this.errors = {};
        this.showFilter = false;
        this.indexEnabled = false;
        this.sortable = true;
    }

    public setDefaultWidth(width: number): void
    {
        this.width = Math.min(3, Math.max(this.minWidth, width));
    }

    public createEntry(data: any): UrlEntry
    {
        return new UrlEntry(data);
    }

    public validate(): Record<string, ValidationErrors>
    {
        this.errors = {};

        return {
            [this.name]: this.errors
        };
    }
}
